import React, { useState } from 'react';
import { supabase } from '../../supabase.js';
import toast from 'react-hot-toast';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

export const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const signIn = async () => {
    if (!email || !password) return toast.error("Please enter your email and password.");
    setIsLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setIsLoading(false);
    if (error) {
      if (error.message === "Invalid login credentials") {
        toast.error("Invalid email or password. If you do not have an account, please sign up.");
      } else {
        toast.error(error.message);
      }
      setPassword('');
      return;
    }

    // Block login if email is not yet confirmed
    const confirmedAt = data?.user?.email_confirmed_at;
    if (!confirmedAt) {
      await supabase.auth.signOut();
      toast.error("Your email has not been verified yet. Please check your inbox and click the confirmation link before signing in.", { duration: 6000 });
      setPassword('');
      return;
    }

    // Only clear the flag AFTER a fully successful, verified login
    localStorage.removeItem('mydrive_pending_verification');
  };

  const handleTabSwitch = (signUpMode) => {
    setIsSignUp(signUpMode);
    setEmail('');
    setPassword('');
    setDisplayName('');
    setShowPassword(false);
  };

  const signUp = async () => {
    if (!email || !password) return toast.error("Please enter your email and password.");
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (!displayName.trim()) return toast.error("Please enter your name.");
    setIsLoading(true);
    const { data, error } = await supabase.auth.signUp({ 
      email: email.trim(), 
      password,
      options: {
        data: {
          display_name: displayName.trim()
        },
        emailRedirectTo: `${window.location.origin}/login`
      }
    });
    if (error) {
      setIsLoading(false);
      return toast.error(error.message);
    }

    // Set flag BEFORE signing out — this flag tells App.js to block auto-login
    // when the user clicks the confirmation link and Supabase creates a session.
    localStorage.setItem('mydrive_pending_verification', 'true');

    // Always sign out after signup — user MUST verify email before they can log in.
    if (data?.session) {
      await supabase.auth.signOut();
    }
    setIsLoading(false);
    toast.success("Registration successful! Please check your email for a confirmation link to activate your account.", { duration: 6000 });
    setEmail('');
    setPassword('');
    setDisplayName('');
    setIsSignUp(false);
  };

  const sendResetEmail = async () => {
    if (!email) return toast.error("Please enter your email address.");
    setIsLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`
    });
    setIsLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Password reset email sent! Please check your inbox.");
      setIsForgotPassword(false);
    }
  };

  const handleSubmit = (e) => {
    if (e.key === 'Enter') {
      if (isForgotPassword) {
        sendResetEmail();
      } else {
        isSignUp ? signUp() : signIn();
      }
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        {isForgotPassword ? (
          <>
            <h2>Reset Password</h2>
            <p>Enter your email address to receive a secure password reset link.</p>
            
            <div className="auth-form">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleSubmit}
                  disabled={isLoading}
                  autoFocus
                />
              </div>
              
              <button 
                className="submit-btn" 
                onClick={sendResetEmail} 
                disabled={isLoading}
              >
                {isLoading ? 'Sending link...' : 'Send Reset Link'}
              </button>
              
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <button 
                  className="back-to-login-btn"
                  onClick={() => setIsForgotPassword(false)}
                  disabled={isLoading}
                >
                  ← Back to Sign In
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <h2>{isSignUp ? 'Create Account' : 'Welcome Back'}</h2>
            <p>{isSignUp ? 'Sign up to start managing your files.' : 'Sign in to your account.'}</p>
            
            <div className="auth-tabs">
              <button 
                className={`auth-tab ${!isSignUp ? 'active' : ''}`} 
                onClick={() => handleTabSwitch(false)}
              >
                Sign In
              </button>
              <button 
                className={`auth-tab ${isSignUp ? 'active' : ''}`} 
                onClick={() => handleTabSwitch(true)}
              >
                Sign Up
              </button>
            </div>

            <div className="auth-form">
              {isSignUp && (
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    placeholder="Your display name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    onKeyDown={handleSubmit}
                    disabled={isLoading}
                  />
                </div>
              )}
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleSubmit}
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label>Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleSubmit}
                    disabled={isLoading}
                    style={{ width: '100%', paddingRight: '48px' }}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              
              {!isSignUp && (
                <div className="auth-secondary-actions">
                  <button 
                    className="auth-link"
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
              
              <button 
                className="submit-btn" 
                onClick={isSignUp ? signUp : signIn} 
                disabled={isLoading}
              >
                {isLoading ? (isSignUp ? 'Creating account...' : 'Signing in...') : (isSignUp ? 'Create Account' : 'Sign In')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};